/**
 * One-time migration: reads decks/cards from localStorage (old format)
 * and uploads them to Supabase for the authenticated user.
 * After a successful migration the localStorage keys are cleared so the
 * migration never runs again.
 */

import { supabase } from "./supabase";
import { Deck } from "@/types/linguadeck";

// Keys that the old localStorage-based version of the app used
const OLD_DECKS_KEYS = ["ld_decks", "linguadeck_decks", "decks"];
const MIGRATION_DONE_KEY = "ld_migration_done";

/** Returns true if the migration has already been completed. */
export function isMigrationDone(): boolean {
  return localStorage.getItem(MIGRATION_DONE_KEY) === "1";
}

/** Reads old decks from localStorage (tries several possible key names). Never returns demo decks. */
function readOldDecks(): Deck[] {
  for (const key of OLD_DECKS_KEYS) {
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const filtered = (parsed as Deck[]).filter(
            (d) => !d.id?.startsWith("deck_demo")
          );
          if (filtered.length > 0) {
            console.log(`[migration] Found ${filtered.length} deck(s) under localStorage key "${key}" (demo decks excluded)`);
            return filtered;
          }
        }
      } catch {
        // ignore parse errors for this key
      }
    }
  }
  return [];
}

/** Uploads a single deck (with its cards) to Supabase. */
async function uploadDeck(userId: string, deck: Deck): Promise<void> {
  const deckId = deck.id && deck.id.startsWith("deck_") ? deck.id : `deck_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

  // Insert deck row
  const { error: deckError } = await supabase.from("decks").insert({
    id: deckId,
    user_id: userId,
    title: deck.title,
    lang: deck.lang,
    emoji: deck.emoji,
    level: deck.level ?? null,
    deck_type: deck.deckType ?? null,
    type: (deck as { type?: string }).type ?? "cards",
  });

  if (deckError) {
    // If the deck already exists (duplicate id), skip it gracefully
    if (deckError.code === "23505") {
      console.warn(`[migration] Deck "${deck.title}" already exists in Supabase – skipping.`);
      return;
    }
    throw deckError;
  }

  // Insert card rows
  if (deck.cards && deck.cards.length > 0) {
    const cardRows = deck.cards.map((card, i) => ({
      id: `card_${deckId}_${i}_${Date.now()}`,
      deck_id: deckId,
      front: card.front,
      back: card.back,
      front_language: ((card as { frontLang?: string }).frontLang?.trim()) || null,
      back_language: ((card as { backLang?: string }).backLang?.trim()) || null,
      hint: card.hint ?? null,
      image_url: (card as { imageUrl?: string | null }).imageUrl ?? null,
      card_type: card.cardType ?? null,
      level: card.level ?? null,
      position: i,
    }));

    const { error: cardsError } = await supabase.from("cards").insert(cardRows);
    if (cardsError) throw cardsError;
  }
}

/** Clears all old localStorage deck keys after a successful migration. */
function clearOldStorage(): void {
  for (const key of OLD_DECKS_KEYS) {
    localStorage.removeItem(key);
  }
  localStorage.setItem(MIGRATION_DONE_KEY, "1");
}

/**
 * Main migration entry-point.
 * Call this once after the user is authenticated.
 *
 * @returns An object describing what happened:
 *   - `migrated`: number of decks successfully uploaded
 *   - `skipped`: number of decks that were already in Supabase
 *   - `errors`: any errors encountered per deck
 */
export async function migrateLocalStorageToSupabase(userId: string): Promise<{
  migrated: number;
  skipped: number;
  errors: string[];
}> {
  const result = { migrated: 0, skipped: 0, errors: [] as string[] };

  if (isMigrationDone()) {
    return result; // already done
  }

  const oldDecks = readOldDecks();

  if (oldDecks.length === 0) {
    clearOldStorage();
    return result;
  }

  console.log(`[migration] Starting migration of ${oldDecks.length} deck(s) for user ${userId}`);

  for (const deck of oldDecks) {
    try {
      await uploadDeck(userId, deck);
      result.migrated++;
      console.log(`[migration] ✅ Migrated deck: "${deck.title}"`);
    } catch (err: any) {
      const msg = err?.message ?? String(err);
      result.errors.push(`"${deck.title}": ${msg}`);
      console.error(`[migration] ❌ Failed to migrate deck "${deck.title}":`, err);
    }
  }

  // Clear old storage only if there were no errors (or all were "already exists")
  if (result.errors.length === 0) {
    clearOldStorage();
    console.log("[migration] ✅ Migration complete. localStorage cleared.");
  } else {
    console.warn("[migration] ⚠️ Migration finished with errors. localStorage NOT cleared.");
  }

  return result;
}
